import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

const API_URL = "/api";

// Configure axios defaults
axios.defaults.baseURL = API_URL;

// Add auth interceptor
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Store
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await axios.post('/auth/login', { email, password });
          const { access_token, user } = response.data;
          localStorage.setItem('token', access_token);
          set({ user, token: access_token, isAuthenticated: true, isLoading: false });
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Login failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      register: async (payload) => {
        set({ isLoading: true, error: null });
        try {
          const body = typeof payload === "object" ? payload : {};
          const response = await axios.post('/auth/register', body);
          const { access_token, user } = response.data;
          localStorage.setItem('token', access_token);
          set({ user, token: access_token, isAuthenticated: true, isLoading: false });
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Registration failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      updateProfile: async (profile) => {
        set({ isLoading: true, error: null });
        try {
          const response = await axios.put('/auth/profile', profile);
          set((state) => ({ user: { ...state.user, ...response.data }, isLoading: false }));
          return { success: true, user: response.data };
        } catch (error) {
          const message = error.response?.data?.detail || 'Profile update failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      deleteAccount: async () => {
        set({ isLoading: true, error: null });
        try {
          await axios.delete('/auth/account');
          localStorage.removeItem('token');
          set({ user: null, token: null, isAuthenticated: false, isLoading: false });
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Account deletion failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      logout: () => {
        localStorage.removeItem('token');
        set({ user: null, token: null, isAuthenticated: false });
      },

      checkAuth: async () => {
        const token = localStorage.getItem('token');
        if (!token) {
          set({ isAuthenticated: false });
          return;
        }
        try {
          const response = await axios.get('/auth/me');
          set({ user: response.data, token, isAuthenticated: true });
        } catch {
          localStorage.removeItem('token');
          set({ user: null, token: null, isAuthenticated: false });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ token: state.token }),
    }
  )
);

// Template Store
export const useTemplateStore = create((set, get) => ({
  templates: [],
  currentTemplate: null,
  isLoading: false,
  error: null,

  setCurrentTemplate: (template) => set({ currentTemplate: template }),

  fetchTemplates: async (publicOnly = false, language = null) => {
    set({ isLoading: true, error: null });
    try {
      const params = { public_only: publicOnly };
      if (language) {
        params.language = language;
      }
      const response = await axios.get('/templates', { params });
      set({ templates: response.data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to fetch templates', isLoading: false });
    }
  },

  fetchTemplate: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.get(`/templates/${id}`);
      set({ currentTemplate: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to fetch template', isLoading: false });
      return null;
    }
  },

  createTemplate: async (templateData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.post('/templates', templateData);
      set((state) => ({ templates: [...state.templates, response.data], isLoading: false }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to create template', isLoading: false });
      return null;
    }
  },

  updateTemplate: async (id, updates) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.put(`/templates/${id}`, updates);
      set((state) => ({
        templates: state.templates.map((t) => (t.id === id ? response.data : t)),
        currentTemplate: response.data,
        isLoading: false,
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to update template', isLoading: false });
      return null;
    }
  },

  deleteTemplate: async (id) => {
    try {
      await axios.delete(`/templates/${id}`);
      set((state) => ({
        templates: state.templates.filter((t) => t.id !== id),
      }));
      return true;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to delete template' });
      return false;
    }
  },
  duplicateTemplate: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.post(`/templates/${id}/duplicate`);
      set((state) => ({
        templates: [response.data, ...state.templates],
        isLoading: false,
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to duplicate template', isLoading: false });
      return null;
    }
  },


  addLayer: async (templateId, layerData) => {
    try {
      const response = await axios.post(`/templates/${templateId}/layers`, layerData);
      set((state) => ({
        currentTemplate: {
          ...state.currentTemplate,
          layers: [...(state.currentTemplate?.layers || []), response.data],
        },
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to add layer' });
      return null;
    }
  },

  updateLayer: async (templateId, layerId, updates) => {
    try {
      const response = await axios.put(`/templates/${templateId}/layers/${layerId}`, updates);
      set((state) => ({
        currentTemplate: {
          ...state.currentTemplate,
          layers: state.currentTemplate?.layers.map((l) =>
            l.id === layerId ? response.data : l
          ),
        },
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to update layer' });
      return null;
    }
  },

  deleteLayer: async (templateId, layerId) => {
    try {
      await axios.delete(`/templates/${templateId}/layers/${layerId}`);
      set((state) => ({
        currentTemplate: {
          ...state.currentTemplate,
          layers: state.currentTemplate?.layers.filter((l) => l.id !== layerId),
        },
      }));
      return true;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to delete layer' });
      return false;
    }
  },

  setCurrentTemplate: (template) => set({ currentTemplate: template }),
  clearError: () => set({ error: null }),
}));

// Render Store
export const useRenderStore = create((set) => ({
  renders: [],
  currentRender: null,
  isLoading: false,
  error: null,

  fetchRenders: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.get('/renders');
      set({ renders: response.data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to fetch renders', isLoading: false });
    }
  },

  createRender: async (templateId, customizations = null, sourceRenderId = null) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.post('/renders', {
        template_id: templateId,
        customizations,
        source_render_id: sourceRenderId,
      });
      set((state) => ({
        renders: [response.data, ...state.renders],
        currentRender: response.data,
        isLoading: false,
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to create render', isLoading: false });
      return null;
    }
  },

  fetchRender: async (id) => {
    try {
      const response = await axios.get(`/renders/${id}`);
      set((state) => ({
        currentRender: response.data,
        renders: state.renders.map((r) => (r.id === id ? response.data : r)),
      }));
      return response.data;
    } catch (error) {
      return null;
    }
  },


  deleteRender: async (id) => {
    try {
      await axios.delete(`/renders/${id}`);
      set((state) => ({
        renders: state.renders.filter((r) => r.id !== id),
        currentRender: state.currentRender?.id === id ? null : state.currentRender,
      }));
      return true;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to delete render' });
      return false;
    }
  },

  pollRenderStatus: async (id, interval = 2000) => {
    const poll = async () => {
      const render = await axios.get(`/renders/${id}`);
      set((state) => ({
        currentRender: render.data,
        renders: state.renders.map((r) => (r.id === id ? render.data : r)),
      }));
      
      if (render.data.status === 'completed' || render.data.status === 'failed') {
        return render.data;
      }
      
      await new Promise((resolve) => setTimeout(resolve, interval));
      return poll();
    };
    
    return poll();
  },

  clearError: () => set({ error: null }),
}));

// Subscription Store
export const useSubscriptionStore = create((set) => ({
  plans: [],
  extraVideoPacks: [],
  paypalClientId: '',
  currentPlan: null,
  isLoading: false,
  error: null,

  fetchPlans: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.get('/subscriptions/plans');
      set({
        plans: response.data?.plans || [],
        extraVideoPacks: response.data?.extra_video_packs || [],
        isLoading: false
      });
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to fetch plans', isLoading: false });
    }
  },

  fetchPayPalConfig: async () => {
    try {
      const response = await axios.get('/subscriptions/paypal/config');
      set({ paypalClientId: response.data?.client_id || '' });
      return response.data;
    } catch (error) {
      set({ paypalClientId: '' });
      return null;
    }
  },

  createSubscription: async (planId, options = {}) => {
    set({ isLoading: true, error: null });
    try {
      const formData = new FormData();
      formData.append('plan_id', planId);
      if (typeof options.vat_applies === 'boolean') {
        formData.append('vat_applies', String(options.vat_applies));
      }
      const response = await axios.post('/subscriptions/create', formData);
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to create subscription', isLoading: false });
      return null;
    }
  },

  captureSubscription: async (subscriptionId) => {
    set({ isLoading: true, error: null });
    try {
      const formData = new FormData();
      formData.append('subscription_id', subscriptionId);
      const response = await axios.post('/subscriptions/capture', formData);
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to capture subscription', isLoading: false });
      return null;
    }
  },

  createExtraVideoOrder: async (packId) => {
    set({ isLoading: true, error: null });
    try {
      const formData = new FormData();
      formData.append('pack_id', packId);
      const response = await axios.post('/subscriptions/extra-videos/create', formData);
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to create add-on order', isLoading: false });
      return null;
    }
  },

  captureExtraVideoOrder: async (orderId) => {
    set({ isLoading: true, error: null });
    try {
      const formData = new FormData();
      formData.append('order_id', orderId);
      const response = await axios.post('/subscriptions/extra-videos/capture', formData);
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Failed to capture add-on order', isLoading: false });
      return null;
    }
  },

  validateVat: async (vatNumber) => {
    try {
      const response = await axios.post('/subscriptions/vat/validate', { vat_number: vatNumber });
      return {
        valid: !!response.data?.valid,
        error: response.data?.error || null,
      };
    } catch (error) {
      return {
        valid: false,
        error: error.response?.data?.detail || 'validation_failed',
      };
    }
  },

  cancelSubscription: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.post('/subscriptions/cancel');
      set({ isLoading: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to cancel subscription';
      set({ error: message, isLoading: false });
      return { success: false, error: message };
    }
  },

  clearError: () => set({ error: null }),
}));

export default {
  useAuthStore,
  useTemplateStore,
  useRenderStore,
  useSubscriptionStore,
};
